import { create } from 'zustand';
import { fetchRecords, queryRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from "../api/fields";
import { useAuthStore } from '../auth/auth-store';

function getIncidentFilter() {
  const inc = useAuthStore.getState().activeIncident;
  return inc ? [{ field: FIELDS.msOrganizations.incidentId, operator: 'eq' as const, value: inc.id }] : [];
}

const FMO = FIELDS.msOrganizations;
const FMP = FIELDS.msReentryPasses;
const FMC = FIELDS.msContacts;

function mapMSOrg(r: Record<string, unknown>): MSOorg {
  return { id: r.record_id as number, orgName: (r[FMO.orgName] as string) || "", opStatus: (r[FMO.opStatus] as string) || "", sector: (r[FMO.sector] as string) || "", contactName: (r[FMO.contactName] as string) || "", contactPhone: (r[FMO.contactPhone] as string) || "", address: (r[FMO.address] as string) || "", city: (r[FMO.city] as string) || "" };
}
function mapMSPass(r: Record<string, unknown>): MSPass {
  return { id: r.record_id as number, authId: (r[FMP.authId] as number) || 0, orgId: (r[FMP.orgId] as number) || 0, orgName: (r[FMP.orgName] as string) || "", passCode: (r[FMP.passCode] as string) || "", status: (r[FMP.status] as string) || "issued", issuedAt: (r[FMP.issuedAt] as string) || "", expiresAt: (r[FMP.expiresAt] as string) || "" };
}
function mapMSContact(r: Record<string, unknown>): MSContact {
  return { id: r.record_id as number, orgId: (r[FMC.orgId] as number) || 0, firstName: (r[FMC.firstName] as string) || "", lastName: (r[FMC.lastName] as string) || "", title: (r[FMC.title] as string) || "", phone: (r[FMC.phone] as string) || "", email: (r[FMC.email] as string) || "" };
}

interface MSOorg { id: number; orgName: string; opStatus: string; sector: string; contactName: string; contactPhone: string; address: string; city: string; [k: string]: unknown; }
interface MSPass { id: number; authId: number; orgId: number; orgName: string; passCode: string; status: string; issuedAt: string; expiresAt: string; [k: string]: unknown; }
interface MSContact { id: number; orgId: number; firstName: string; lastName: string; title: string; phone: string; email: string; [k: string]: unknown; }

interface MainStreetState {
  organizations: MSOorg[]; passes: MSPass[]; contacts: MSContact[]; loading: boolean;
  fetchOrganizations: () => Promise<void>; fetchPasses: () => Promise<void>; fetchContacts: () => Promise<void>;
  createOrganization: (data: Record<string, unknown>) => Promise<number | null>;
  createPass: (data: Record<string, unknown>) => Promise<number | null>;
  updatePass: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteOrganization: (id: number) => Promise<void>;
  updateOrganization: (id: number, data: Record<string, unknown>) => Promise<void>;
  // V1 compat
  loadOrganizations: () => Promise<void>;
  loadingOrgs: boolean;
  loadContacts: () => Promise<void>;
  createContact: (data: any) => Promise<number>;
  updateContact: (id: any, data: any) => Promise<void>;
  deleteContact: (id: any) => Promise<void>;
  facilities: any[];
  loadFacilities: () => Promise<void>;
  orgLists: any[];
  loadOrgLists: () => Promise<void>;
  createOrgList: (data: any) => Promise<number>;
  updateOrgList: (id: any, data: any) => Promise<void>;
  deleteOrgList: (id: any) => Promise<void>;
  loadPasses: () => Promise<void>;
}

export const useMainStreetStore = create<MainStreetState>((set, get) => ({
  organizations: [], passes: [], contacts: [], loading: false,
  fetchOrganizations: async () => {
    set({ loading: true });
    try {
      const filters = getIncidentFilter();
      const r = filters.length
        ? await queryRecords<MSOorg>(TABLES.msOrganizations, { filters, limit: 500 })
        : await fetchRecords<MSOorg>(TABLES.msOrganizations, { limit: 500 });
      set({ organizations: r.records.map(mapMSOrg) });
    } catch (e) { console.error('[MS]', e); }
    set({ loading: false });
  },
  fetchPasses: async () => { try { const r = await fetchRecords<MSPass>(TABLES.msReentryPasses, { limit: 1000, sort: { field: 'created_at', direction: 'desc' } }); set({ passes: r.records.map(mapMSPass) }); } catch (e) { console.error('[MS]', e); } },
  fetchContacts: async () => { try { const r = await fetchRecords<MSContact>(TABLES.msContacts, { limit: 500 }); set({ contacts: r.records.map(mapMSContact) }); } catch (e) { console.error('[MS]', e); } },
  createOrganization: async (data) => {
    const p: Record<string,unknown> = {};
    if (data.orgName) p[FMO.orgName] = data.orgName; if (data.opStatus) p[FMO.opStatus] = data.opStatus;
    if (data.sector) p[FMO.sector] = data.sector; if (data.contactName) p[FMO.contactName] = data.contactName;
    if (data.contactPhone) p[FMO.contactPhone] = data.contactPhone; if (data.address) p[FMO.address] = data.address;
    if (data.city) p[FMO.city] = data.city; if (data.contactEmail) p[FMO.contactEmail] = data.contactEmail;
    try { const r = await createRecord<MSOorg>(TABLES.msOrganizations, p); await get().fetchOrganizations(); return r.id; } catch (e) { console.error('[MS]', e); return null; }
  },
  createPass: async (data) => {
    const p: Record<string,unknown> = {};
    if (data.authId) p[FMP.authId] = data.authId; if (data.orgId) p[FMP.orgId] = data.orgId;
    if (data.orgName) p[FMP.orgName] = data.orgName; if (data.passCode) p[FMP.passCode] = data.passCode;
    if (data.status) p[FMP.status] = data.status; if (data.issuedAt) p[FMP.issuedAt] = data.issuedAt;
    try { const r = await createRecord<MSPass>(TABLES.msReentryPasses, p); await get().fetchPasses(); return r.id; } catch (e) { console.error('[MS]', e); return null; }
  },
  updatePass: async (id, data) => {
    const p: Record<string,unknown> = {};
    if (data.status) p[FMP.status] = data.status;
    try { await updateRecord(TABLES.msReentryPasses, id, p); await get().fetchPasses(); } catch (e) { console.error('[MS]', e); }
  },
  deleteOrganization: async (id) => {
    try { await deleteRecord(TABLES.msOrganizations, id); await get().fetchOrganizations(); } catch (e) { console.error('[MS]', e); }
  },
  updateOrganization: async (id, data) => {
    const p: Record<string,unknown> = {};
    if (data.orgName) p[FMO.orgName] = data.orgName; if (data.opStatus) p[FMO.opStatus] = data.opStatus;
    if (data.sector) p[FMO.sector] = data.sector; if (data.contactName) p[FMO.contactName] = data.contactName;
    if (data.contactPhone) p[FMO.contactPhone] = data.contactPhone; if (data.address) p[FMO.address] = data.address;
    if (data.city) p[FMO.city] = data.city;
    try { await updateRecord(TABLES.msOrganizations, id, p); await get().fetchOrganizations(); } catch (e) { console.error('[MS]', e); }
  },

  // V1 compat aliases
  loadOrganizations: async () => get().fetchOrganizations(),
  loadingOrgs: false,
  loadContacts: async () => { await get().fetchContacts(); },
  createContact: async (_data: any) => 0,
  updateContact: async (_id: any, _data: any) => {},
  deleteContact: async (_id: any) => {},
  facilities: [] as any[],
  loadFacilities: async () => {},
  orgLists: [] as any[],
  loadOrgLists: async () => {},
  createOrgList: async (_data: any) => 0,
  updateOrgList: async (_id: any, _data: any) => {},
  deleteOrgList: async (_id: any) => {},
  loadPasses: async () => { await get().fetchPasses(); },
}));
